import React, { useEffect, useState, useCallback } from 'react';
import { Container, Card, Button, Row, Col, Alert, Spinner } from 'react-bootstrap';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { 
  FaEdit, FaTrash, FaPlus, FaMapMarkerAlt, FaPhone, FaGlobe, FaStar, 
  FaBars, FaTimes, FaBuilding, FaChartLine, FaEye, FaComments,
} from 'react-icons/fa';
import { businessApi } from '../services/api';
import { ROUTES, getImageUrl } from '../constants/Constants';
import { useAuth } from '../context/AuthContext';
import './Dashboard.css';
import Sidebar from '../components/Sidebar';
import DashboardCharts from '../components/DashboardCharts';
import AnalyticsCharts from '../components/AnalyticsCharts';
import RealTimeMetrics from '../components/RealTimeMetrics';

// Default image function
const getDefaultImage = (category) => {
  const categoryImages = {
    'Restaurant': '/assets/images/restaurant-default.jpg',
    'Hotel': '/assets/images/hotel-default.jpg',
    'Shop': '/assets/images/shop-default.jpg',
    'Service': '/assets/images/service-default.jpg',
    'default': '/assets/images/default-business.jpg'
  };
  return categoryImages[category] || categoryImages.default;
};

const Dashboard = () => {
  const [userBusinesses, setUserBusinesses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dashboardStats, setDashboardStats] = useState({
    totalBusinesses: 0,
    totalViews: 0,
    totalReviews: 0,
    totalMessages: 0,
    recentActivity: []
  });
  const { user } = useAuth();
  const navigate = useNavigate();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [autoRefresh, setAutoRefresh] = useState(true);

  const handleAddBusiness = () => navigate(ROUTES.BUSINESS.ADD);

  // Function to manually refresh data
  const fetchUserBusinesses = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      console.log('=== DASHBOARD DEBUG START ===');
      console.log('User object:', user);
      console.log('User ID:', user?.id);
      console.log('API Base URL:', process.env.REACT_APP_API_BASE_URL);
      console.log('Business API endpoint:', '/businesses.php');
      console.log('Local storage token:', localStorage.getItem('token'));
      console.log('Local storage user:', localStorage.getItem('user'));
      
      // Test API connectivity first
      try {
        const testUrl = 'https://backend.leadslisting.in/api/businesses.php';
        console.log('Testing API connectivity to:', testUrl);
        const testResponse = await fetch(testUrl);
        console.log('Test response status:', testResponse.status);
        console.log('Test response ok:', testResponse.ok);
        
        if (!testResponse.ok) {
          throw new Error(`API returned status: ${testResponse.status}`);
        }
      } catch (testErr) {
        console.error('API connectivity test failed:', testErr);
        throw new Error(`API connectivity test failed: ${testErr.message}`);
      }
      
      console.log('Fetching businesses for user:', user?.id);
      const allBusinesses = await businessApi.fetchAll();
      console.log('All businesses fetched:', allBusinesses);
      console.log('Total businesses in API:', allBusinesses.length);
      console.log('Businesses type:', typeof allBusinesses);
      console.log('Is array:', Array.isArray(allBusinesses));
      
      // Filter businesses owned by the logged-in user
      const myBusinesses = allBusinesses.filter(b => {
        const userIds = [
          b.userId, b.userid, b.ownerId, b.ownerid, b.user_id
        ].map(id => String(id));
        const userStringId = String(user?.id);
        
        const matches = userIds.some(id => id === userStringId);
        
        if (matches) {
          console.log('Business matches user:', {
            businessId: b.id,
            businessName: b.name || b.businessName,
            userIds: userIds,
            userStringId: userStringId
          });
        }
        
        return matches;
      });
      console.log('Filtered user businesses:', myBusinesses);
      console.log('User businesses count:', myBusinesses.length);
      console.log('User ID being filtered:', user?.id);
      console.log('Sample business userIds:', allBusinesses.slice(0, 3).map(b => ({ 
        id: b.id, 
        userId: b.userId, 
        userid: b.userid, 
        ownerId: b.ownerId,
        ownerid: b.ownerid,
        user_id: b.user_id 
      })));
      
      // Temporary fallback for debugging - if no user businesses found, show all
      const finalBusinesses = myBusinesses.length > 0 ? myBusinesses : allBusinesses.slice(0, 5);
      console.log('Final businesses to display:', finalBusinesses.length);
      
      setUserBusinesses(finalBusinesses);
      
      // Calculate dynamic dashboard stats from real data
      const totalViews = finalBusinesses.reduce((sum, business) => sum + (business?.view_count || business?.views || 0), 0);
      const totalReviews = finalBusinesses.reduce((sum, business) => sum + (business?.reviews_count || 0), 0);
      const totalContacts = finalBusinesses.reduce((sum, business) => sum + (business?.contacts || business?.leads || 0), 0);
      const totalFavorites = finalBusinesses.reduce((sum, business) => sum + (business?.favorites || business?.likes || 0), 0);
      
      // Calculate average rating dynamically
      const totalRating = finalBusinesses.reduce((sum, business) => sum + (business?.rating || 0), 0);
      const avgRating = finalBusinesses.length > 0 ? (totalRating / finalBusinesses.length).toFixed(1) : 0;
      
      // Generate dynamic recent activity from real business data
      const recentActivity = finalBusinesses
        .sort((a, b) => new Date(b?.created_at || b?.updated_at) - new Date(a?.created_at || a?.updated_at))
        .slice(0, 5)
        .map(business => ({
          id: business?.id,
          type: 'business_created',
          title: business?.name || business?.businessName || 'Business',
          date: business?.created_at || business?.updated_at,
          description: `Business listing ${business?.created_at ? 'created' : 'updated'}`,
          views: business?.view_count || business?.views || 0,
          rating: business?.rating || 0
        }));
      
      const stats = {
        totalBusinesses: finalBusinesses.length,
        totalViews,
        totalReviews,
        totalContacts,
        totalFavorites,
        avgRating: parseFloat(avgRating),
        recentActivity
      };
      
      console.log('Dashboard stats calculated:', stats);
      setDashboardStats(stats);
      setLastUpdated(new Date().toLocaleTimeString());
      
    } catch (err) {
      console.error('=== API ERROR DEBUG ===');
      console.error('Error fetching businesses:', err);
      console.error('Error message:', err.message);
      console.error('Error stack:', err.stack);
      
      // Try alternative API endpoint as fallback
      try {
        console.log('=== TRYING FALLBACK API ===');
        const fallbackUrl = 'https://backend.leadslisting.in/api/businesses.php';
        console.log('Fallback URL:', fallbackUrl);
        
        const response = await fetch(fallbackUrl);
        console.log('Fallback response status:', response.status);
        console.log('Fallback response ok:', response.ok);
        
        const fallbackData = await response.json();
        console.log('Fallback data:', fallbackData);
        
        // Handle the API response structure
        const allBusinesses = fallbackData.data || fallbackData || [];
        console.log('Fallback businesses:', allBusinesses);
        console.log('Fallback businesses type:', typeof allBusinesses);
        console.log('Fallback businesses is array:', Array.isArray(allBusinesses));
        
        if (Array.isArray(allBusinesses)) {
          const myBusinesses = allBusinesses.filter(
            b => String(b.userId) === String(user.id) ||
                 String(b.userid) === String(user.id) ||
                 String(b.ownerId) === String(user.id) ||
                 String(b.ownerid) === String(user.id) ||
                 String(b.user_id) === String(user.id)
          );
          console.log('Fallback filtered businesses:', myBusinesses);
          setUserBusinesses(myBusinesses);
          setError(null);
          console.log('Fallback API successful:', myBusinesses);
        } else {
          console.log('Fallback data is not an array:', typeof allBusinesses);
          throw new Error('Fallback API returned invalid data');
        }
      } catch (fallbackErr) {
        console.error('=== FALLBACK API ERROR ===');
        console.error('Fallback API also failed:', fallbackErr);
        console.error('Fallback error message:', fallbackErr.message);
        setError(`Failed to load your businesses. Please check your connection and try again.`);
        setUserBusinesses([]);
      }
    } finally {
      setLoading(false);
      console.log('=== DASHBOARD DEBUG END ===');
    }
  }, [user]);

  useEffect(() => {
    if (!user || !user?.id) {
      navigate('/login');
      return;
    }
    fetchUserBusinesses();
  }, [user, navigate, fetchUserBusinesses]);

  // Add effect to close sidebar on route/user change
  useEffect(() => {
    setIsMobileSidebarOpen(false);
  }, [user, navigate]);

  // Auto-refresh dashboard data every 30 seconds
  useEffect(() => {
    if (!autoRefresh) return;
    
    const interval = setInterval(() => {
      console.log('Auto-refreshing dashboard data...');
      if (user?.id) {
        fetchUserBusinesses();
      }
    }, 30000); // 30 seconds

    return () => clearInterval(interval);
  }, [autoRefresh, user?.id, fetchUserBusinesses]);

  const handleEdit = (id) => navigate(ROUTES.BUSINESS.EDIT.replace(':id', id));
  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this business?')) {
      try {
        await businessApi.delete(id);
        setUserBusinesses(userBusinesses.filter(b => b.id !== id));
      } catch (err) {
        alert('Failed to delete business.');
      }
    }
  };

  if (!user) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '100vh' }}>
        <div className="spinner-border" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="dashboard-container">
      <Row className="g-0">
        {/* Desktop Sidebar */}
        <Col md={3} lg={2} className="d-none d-md-block">
          <Sidebar 
            collapsed={sidebarCollapsed} 
            onToggle={() => setSidebarCollapsed(!sidebarCollapsed)}
          />
        </Col>

        {/* Mobile Sidebar Toggle */}
        {window.innerWidth <= 768 && (
          <>
            <button
              className="mobile-sidebar-toggle-btn"
              style={{
                position: 'fixed',
                top: 20,
                left: 20,
                zIndex: 2100,
                background: 'white',
                border: 'none',
                borderRadius: 8,
                boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                fontSize: 28,
                width: 48,
                height: 48,
                display: isMobileSidebarOpen ? 'none' : 'block',
              }}
              onClick={() => setIsMobileSidebarOpen(true)}
            >
              <FaBars />
            </button>
            {isMobileSidebarOpen && (
              <>
                <div
                  className="sidebar-backdrop"
                  style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    width: '100vw',
                    height: '100vh',
                    background: 'rgba(0,0,0,0.4)',
                    zIndex: 1999,
                  }}
                  onClick={() => setIsMobileSidebarOpen(false)}
                />
                <div
                  className="sidebar-overlay"
                  style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    height: '100vh',
                    width: '80vw',
                    maxWidth: 300,
                    background: '#222',
                    color: 'white',
                    zIndex: 2000,
                    boxShadow: '2px 0 16px rgba(0,0,0,0.2)',
                    padding: '20px',
                    overflowY: 'auto',
                  }}
                >
                  <div className="d-flex justify-content-between align-items-center mb-4">
                    <h5>Menu</h5>
                    <button
                      onClick={() => setIsMobileSidebarOpen(false)}
                      style={{
                        background: 'none',
                        border: 'none',
                        color: 'white',
                        fontSize: '24px',
                        cursor: 'pointer',
                      }}
                    >
                      <FaTimes />
                    </button>
                  </div>
                  <Sidebar 
                    collapsed={false} 
                    onToggle={() => setIsMobileSidebarOpen(false)}
                    isMobile={true}
                  />
                </div>
              </>
            )}
          </>
        )}

        {/* Main Content */}
        <Col md={9} lg={10}>
          <Container fluid className="p-4">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
            >
              {/* Dashboard Header */}
              <div className="dashboard-header">
                <div className="d-flex justify-content-between align-items-center mb-4">
                  <div>
                    <h1 className="dashboard-title">Welcome back, {user?.name || 'User'}!</h1>
                    <p className="dashboard-subtitle">Here's what's happening with your businesses today</p>
                    {lastUpdated && (
                      <small className="text-white-50">
                        Last updated: {lastUpdated}
                      </small>
                    )}
                  </div>
                  <div className="d-flex gap-2">
                    <Button
                      variant="outline-light"
                      size="sm"
                      onClick={() => {
                        console.log('Manual refresh triggered');
                        fetchUserBusinesses();
                      }}
                    >
                      <FaChartLine className="me-1" />
                      Refresh
                    </Button>
                    <Button
                      variant={autoRefresh ? "success" : "outline-light"}
                      size="sm"
                      onClick={() => setAutoRefresh(!autoRefresh)}
                    >
                      {autoRefresh ? "Auto ON" : "Auto OFF"}
                    </Button>
                    <Button className="add-business-btn" onClick={handleAddBusiness}>
                      <FaPlus className="me-2" />
                      Add New Business
                    </Button>
                  </div>
                </div>
              </div>

              {loading ? (
                <div className="loading-container">
                  <Spinner animation="border" className="loading-spinner" />
                  <p className="mt-3 text-white">Loading your dashboard...</p>
                </div>
              ) : error ? (
                <Alert variant="danger" className="error-alert">
                  <div className="d-flex justify-content-between align-items-center">
                    <span>{error}</span>
                    <Button 
                      variant="outline-danger" 
                      size="sm"
                      onClick={() => fetchUserBusinesses()}
                    >
                      Retry
                    </Button>
                  </div>
                </Alert>
              ) : (
                <>
                  {/* Statistics Cards */}
                  <Row className="mb-4">
                    <Col md={3} className="mb-3">
                      <motion.div
                        initial={{ opacity: 0, scale: 0.9 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ delay: 0.1 }}
                      >
                        <Card className="stat-card">
                          <Card.Body className="text-center">
                            <div className="stat-icon">
                              <FaBuilding />
                            </div>
                            <h3 className="stat-number">{dashboardStats.totalBusinesses}</h3>
                            <p className="stat-label">Total Businesses</p>
                          </Card.Body>
                        </Card>
                      </motion.div>
                    </Col>
                    <Col md={3} className="mb-3">
                      <motion.div
                        initial={{ opacity: 0, scale: 0.9 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ delay: 0.2 }}
                      >
                        <Card className="stat-card">
                          <Card.Body className="text-center">
                            <div className="stat-icon">
                              <FaEye />
                            </div>
                            <h3 className="stat-number">{dashboardStats.totalViews}</h3>
                            <p className="stat-label">Total Views</p>
                          </Card.Body>
                        </Card>
                      </motion.div>
                    </Col>
                    <Col md={3} className="mb-3">
                      <motion.div
                        initial={{ opacity: 0, scale: 0.9 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ delay: 0.3 }}
                      >
                        <Card className="stat-card">
                          <Card.Body className="text-center">
                            <div className="stat-icon">
                              <FaComments />
                            </div>
                            <h3 className="stat-number">{dashboardStats.totalReviews}</h3>
                            <p className="stat-label">Total Reviews</p>
                          </Card.Body>
                        </Card>
                      </motion.div>
                    </Col>
                    <Col md={3} className="mb-3">
                      <motion.div
                        initial={{ opacity: 0, scale: 0.9 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ delay: 0.4 }}
                      >
                        <Card className="stat-card">
                          <Card.Body className="text-center">
                            <div className="stat-icon">
                              <FaStar />
                            </div>
                            <h3 className="stat-number">{dashboardStats.avgRating}</h3>
                            <p className="stat-label">Average Rating</p>
                          </Card.Body>
                        </Card>
                      </motion.div>
                    </Col>
                  </Row>

                  {/* Charts Section */}
                  <Row className="mb-4">
                    <Col lg={8} className="mb-4">
                      <motion.div
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: 0.5 }}
                      >
                        <Card className="chart-card">
                          <Card.Header>
                            <h5 className="mb-0">Business Performance</h5>
                          </Card.Header>
                          <Card.Body>
                            <DashboardCharts userBusinesses={userBusinesses} />
                          </Card.Body>
                        </Card>
                      </motion.div>
                    </Col>
                    <Col lg={4} className="mb-4">
                      <motion.div
                        initial={{ opacity: 0, x: 20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: 0.6 }}
                      >
                        <Card className="chart-card">
                          <Card.Header>
                            <h5 className="mb-0">Analytics</h5>
                          </Card.Header>
                          <Card.Body>
                            <AnalyticsCharts userBusinesses={userBusinesses} />
                          </Card.Body>
                        </Card>
                      </motion.div>
                    </Col>
                  </Row>

                  {/* Real-time Metrics */}
                  <Row className="mb-4">
                    <Col>
                      <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.7 }}
                      >
                        <Card className="metrics-card">
                          <Card.Header>
                            <h5 className="mb-0">Real-time Metrics</h5>
                          </Card.Header>
                          <Card.Body>
                            <RealTimeMetrics userBusinesses={userBusinesses} />
                          </Card.Body>
                        </Card>
                      </motion.div>
                    </Col>
                  </Row>

                  {/* Recent Activity */}
                  <Row className="mb-4">
                    <Col lg={6} className="mb-4">
                      <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.8 }}
                      >
                        <Card className="activity-card">
                          <Card.Header>
                            <h5 className="mb-0">Recent Activity</h5>
                          </Card.Header>
                          <Card.Body>
                            {dashboardStats.recentActivity?.length > 0 ? (
                              <div className="activity-list">
                                {dashboardStats.recentActivity.map((activity, index) => (
                                  <div key={index} className="activity-item">
                                    <div className="activity-icon">
                                      <FaBuilding />
                                    </div>
                                    <div className="activity-content">
                                      <h6 className="activity-title">{activity.title}</h6>
                                      <p className="activity-description">{activity.description}</p>
                                      <small className="activity-date">{activity.date}</small>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            ) : (
                              <p className="text-muted">No recent activity</p>
                            )}
                          </Card.Body>
                        </Card>
                      </motion.div>
                    </Col>
                    <Col lg={6} className="mb-4">
                      <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.9 }}
                      >
                        <Card className="business-grid-card">
                          <Card.Header>
                            <h5 className="mb-0">Your Businesses</h5>
                          </Card.Header>
                          <Card.Body>
                            {userBusinesses?.length > 0 ? (
                              <Row>
                                {userBusinesses.slice(0, 4).map((business, index) => (
                                  <Col key={business?.id || index} md={6} className="mb-3">
                                    <motion.div
                                      initial={{ opacity: 0, scale: 0.9 }}
                                      animate={{ opacity: 1, scale: 1 }}
                                      transition={{ delay: 0.1 * index }}
                                    >
                                      <Card className="business-card">
                                        <Card.Body>
                                          <div className="business-image">
                                            <img
                                              src={business?.profile_photo ? getImageUrl(business.profile_photo) : getDefaultImage(business?.category)}
                                              alt={business?.name || business?.businessName}
                                              onError={(e) => {
                                                e.target.src = getDefaultImage(business?.category);
                                              }}
                                            />
                                          </div>
                                          <h6 className="business-title">
                                            {business?.name || business?.businessName}
                                          </h6>
                                          <p className="business-category">{business?.category}</p>
                                          <div className="business-stats">
                                            <span className="stat">
                                              <FaEye className="me-1" />
                                              {business?.view_count || business?.views || 0}
                                            </span>
                                            <span className="stat">
                                              <FaStar className="me-1" />
                                              {business?.rating || 0}
                                            </span>
                                          </div>
                                        </Card.Body>
                                      </Card>
                                    </motion.div>
                                  </Col>
                                ))}
                              </Row>
                            ) : (
                              <div className="text-center">
                                <FaBuilding size={48} className="text-muted mb-3" />
                                <p className="text-muted">No businesses found</p>
                                <Button variant="primary" onClick={handleAddBusiness}>
                                  <FaPlus className="me-2" />
                                  Add Your First Business
                                </Button>
                              </div>
                            )}
                          </Card.Body>
                        </Card>
                      </motion.div>
                    </Col>
                  </Row>

                  {/* Business List */}
                  {userBusinesses?.length > 0 && (
                    <Row>
                      <Col>
                        <motion.div
                          initial={{ opacity: 0, y: 20 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ delay: 1.0 }}
                        >
                          <Card className="business-list-card">
                            <Card.Header>
                              <h5 className="mb-0">All Your Businesses</h5>
                            </Card.Header>
                            <Card.Body>
                              <Row>
                                {userBusinesses.map((business, index) => (
                                  <Col key={business?.id || index} lg={4} md={6} className="mb-4">
                                    <motion.div
                                      initial={{ opacity: 0, scale: 0.9 }}
                                      animate={{ opacity: 1, scale: 1 }}
                                      transition={{ delay: 0.1 * index }}
                                    >
                                      <Card className="business-card">
                                        <div className="business-image">
                                          <img
                                            src={business?.profile_photo ? getImageUrl(business.profile_photo) : getDefaultImage(business?.category)}
                                            alt={business?.name || business?.businessName}
                                            onError={(e) => {
                                              e.target.src = getDefaultImage(business?.category);
                                            }}
                                          />
                                        </div>
                                        <Card.Body>
                                          <h6 className="business-title">
                                            {business?.name || business?.businessName}
                                          </h6>
                                          <p className="business-category">{business?.category}</p>
                                          <p className="business-description">
                                            {business?.description?.substring(0, 100)}...
                                          </p>
                                          <div className="business-info">
                                            <div className="info-item">
                                              <FaMapMarkerAlt className="me-1" />
                                              {business?.address}
                                            </div>
                                            {business?.phone && (
                                              <div className="info-item">
                                                <FaPhone className="me-1" />
                                                {business?.phone}
                                              </div>
                                            )}
                                            {business?.website && (
                                              <div className="info-item">
                                                <FaGlobe className="me-1" />
                                                {business?.website}
                                              </div>
                                            )}
                                          </div>
                                          <div className="business-stats">
                                            <span className="stat">
                                              <FaEye className="me-1" />
                                              {business?.view_count || business?.views || 0}
                                            </span>
                                            <span className="stat">
                                              <FaStar className="me-1" />
                                              {business?.rating || 0}
                                            </span>
                                          </div>
                                          <div className="business-actions">
                                            <Button
                                              variant="outline-primary"
                                              size="sm"
                                              className="flex-fill me-2"
                                              onClick={() => navigate(`/business/${business?.id}`)}
                                            >
                                              <FaEye className="me-1" />
                                              View
                                            </Button>
                                            <Button
                                              variant="outline-secondary"
                                              size="sm"
                                              className="flex-fill me-2"
                                              onClick={() => handleEdit(business?.id)}
                                            >
                                              <FaEdit className="me-1" />
                                              Edit
                                            </Button>
                                            <Button
                                              variant="outline-danger"
                                              size="sm"
                                              className="flex-fill"
                                              onClick={() => handleDelete(business?.id)}
                                            >
                                              <FaTrash className="me-1" />
                                              Delete
                                            </Button>
                                          </div>
                                        </Card.Body>
                                      </Card>
                                    </motion.div>
                                  </Col>
                                ))}
                              </Row>
                            </Card.Body>
                          </Card>
                        </motion.div>
                      </Col>
                    </Row>
                  )}
                </>
              )}
            </motion.div>
          </Container>
        </Col>
      </Row>
    </div>
  );
};

export default Dashboard;